import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/forms/input.component';
import { useForm, Controller } from 'react-hook-form';
import Button from '../../../components/forms/button.component';
import { AlertService } from '../../../services/alert.service';
import useModal from '../../../hooks/use-close-modal.hook';
import { STORAGE } from '../../../constants';
import { LocalStorageService } from '../../../services/local-storage.service';
import LogData from '../../../components/logs/log-data.component';
import { AccountsService } from '../../../services/accounts.service';
import Icon from '../../../components/forms/icon.component';
import Select from 'react-select';
import InvalidFeedback from '../../../components/forms/invalid-feedback.component';
import useInputValidation from '../../../hooks/use-input-validation.hook';

interface Option {
  value: string;
  label: string;
}

interface AddChannelModalProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: {
    channelType: string;
    options: Option[];
    existingConfigs: string[];
    accountId: string;
    existingChannels: Array<{
      config: string;
      price: number;
    }>;
  };
}

interface FormData {
  config: string;
  price: number;
}

interface BaseChannelConfig {
  icon: string;
  title: string;
  logo: string;
  updateService: string;
  subtitle?: string;
  rcsType?: string;
}

const CHANNEL_CONFIG: Record<string, BaseChannelConfig> = {
  whatsapp: {
    icon: 'ki-whatsapp',
    title: 'WhatsApp',
    logo: '/assets/media/logos/meta.png',
    updateService: 'updateWAChannel'
  },
  sms: {
    icon: 'ki-message-notif',
    title: 'SMS',
    logo: '/assets/media/logos/sms-logo.png',
    updateService: 'updateSMSChannel'
  },
  rcs_conversational: {
    icon: 'ki-messages',
    title: 'RCS',
    subtitle: 'Conversacional',
    logo: '/assets/media/logos/rcs-logo.png',
    updateService: 'updateRCSChannel',
    rcsType: 'conversational'
  },
  rcs_single: {
    icon: 'ki-messages',
    title: 'RCS',
    subtitle: 'Simple',
    logo: '/assets/media/logos/rcs-logo.png',
    updateService: 'updateRCSChannel',
    rcsType: 'single'
  },
  rcs_basic: {
    icon: 'ki-messages',
    title: 'RCS',
    subtitle: 'Básico',
    logo: '/assets/media/logos/rcs-logo.png',
    updateService: 'updateRCSChannel',
    rcsType: 'basic'
  }
};

function AddChannelModal({ show, onRequestClose, payload }: AddChannelModalProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const { priceRegex, handlePrice } = useInputValidation();
  useModal(modalRef, show);
  const type = payload?.channelType;
  const user = LocalStorageService.get(STORAGE.SESSION);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid }
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: { config: '', price: 1 }
  });

  const [isProcessing, setIsProcessing] = useState(false);

  const resetForm = () => {
    setIsProcessing(false);
    reset({ config: '', price: 1 });
  };

  const config = CHANNEL_CONFIG[payload?.channelType as keyof typeof CHANNEL_CONFIG];
  const availableOptions =
    payload?.options?.filter((option) => !payload?.existingConfigs?.includes(option.value)) || [];

  const getLabelAndPlaceholder = () => {
    if (payload?.channelType === 'sms') {
      return {
        label: t('account-page.modal-add-account.stepper-3-input-mask'),
        placeholder: t('account-page.modal-add-account.stepper-3-input-mask-placeholder')
      };
    }

    const isRCS = ['rcs_conversational', 'rcs_single', 'rcs_basic'].includes(
      payload?.channelType || ''
    );
    return {
      label: isRCS
        ? t('account-page.modal-add-account.stepper-3-input-agents')
        : t('account-page.modal-add-account.stepper-3-input-accounts'),
      placeholder: isRCS
        ? t('account-page.modal-add-account.stepper-3-input-agents-placeholder')
        : t('account-page.modal-add-account.stepper-3-input-accounts-placeholder')
    };
  };

  const { label, placeholder } = getLabelAndPlaceholder();

  const onSubmit = async (data: FormData) => {
    if (!data.config) {
      AlertService.toastError(t('forms.invalid-FeedBack.required'));
      return;
    }

    if (data.price <= 0) {
      AlertService.toastError(t('forms.invalid-FeedBack.min-price'));
      return;
    }

    setIsProcessing(true);

    try {
      const newChannel = { config: data.config, price: parseFloat(data.price.toString()) };

      const existingChannels = payload.existingChannels || [];
      const allChannels = [...existingChannels, newChannel];

      const channelType = payload.channelType;
      if (channelType === 'whatsapp') {
        await AccountsService.updateWAChannel({
          account: payload.accountId,
          username: user.username,
          whatsapp: allChannels
        });
      } else if (channelType === 'sms') {
        await AccountsService.updateSMSChannel({
          account: payload.accountId,
          username: user.username,
          sms: allChannels
        });
      } else if (config && 'rcsType' in config) {
        await AccountsService.updateRCSChannel({
          account: payload.accountId,
          username: user.username,
          type: config.rcsType,
          rcs: allChannels
        });
      } else if (channelType === 'email') {
        await AccountsService.updateMailingChannel({
          account: payload.accountId,
          username: user.username,
          email: allChannels
        });
      }

      onRequestClose(true);
      AlertService.toastSuccess(t('account-page.alert-success-add'));
      resetForm();
      LogData(
        { channelType, config: data.config, price: data.price },
        `addChannel-${payload.accountId}-${channelType}`,
        window.location.href
      );
    } catch (error: any) {
      console.error('Error adding channel:', error);
      AlertService.toastError(error.error || error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div aria-hidden="true" className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog modal-mw-900px  modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => {
                onRequestClose(false);
                resetForm();
              }}
            >
              <Icon icon="ki-cross" size="fs-1" />
            </button>
          </div>
          <div className="modal-body pt-0 pb-0 px-5 px-xl-20">
            <div className="d-flex align-items-center justify-content-center">
              {/* <img src={config.logo} alt={config.title} width="40" className="me-3 position" /> */}
              <div className="d-flex flex-column justify-content-center">
                <h3 className="modal-title text-center">
                  {type === 'whatsapp'
                    ? t('account-page.add-account-btn')
                    : type === 'sms'
                      ? t('account-page.add-mask-btn')
                      : type === 'email'
                        ? t('account-page.add-account-btn')
                        : t('account-page.add-agent-btn')}
                  : <br />
                  <span className="text-primary">
                    {config?.title} {}
                    {config?.subtitle && config?.subtitle}
                  </span>
                </h3>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <label className="form-label fw-bold">
                  {label}
                  <span className="required"></span>
                </label>

                <Controller
                  control={control}
                  name="config"
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <Select
                        options={availableOptions}
                        noOptionsMessage={() => t('general.no-options')}
                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                        classNamePrefix="form-select-custom"
                        placeholder={placeholder}
                        onChange={(selectedOption) =>
                          onChange(selectedOption ? selectedOption.value : '')
                        }
                        value={payload?.options?.find((op) => op.value === value) || null}
                      />
                      {error && <InvalidFeedback error={error} />}
                    </>
                  )}
                />

                <Input
                  label={t('account-page.modal-add-account.stepper-3-input-price')}
                  requiredIndicator="required"
                  placeholder={t(
                    'account-page.modal-add-account.stepper-3-input-price-placeholder'
                  )}
                  onKeyDown={handlePrice}
                  errors={errors.price}
                  {...register('price', {
                    required: true,
                    pattern: {
                      value: priceRegex,
                      message: t('forms.invalid-FeedBack.valid-price')
                    },
                    min: {
                      value: 0.01,
                      message: t('forms.invalid-FeedBack.min-price')
                    },
                    setValueAs: (value) => parseFloat(value)
                  })}
                />
              </div>

              {availableOptions.length === 0 && (
                <div className="alert alert-warning mt-4">
                  <Icon icon="ki-information-5" size="fs-2" className="me-2" />
                  {t('account-page.no-available-options')}
                </div>
              )}

              <div className="modal-footer border-top-0 justify-content-center mt-5">
                <Button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    onRequestClose(false);
                    resetForm();
                  }}
                >
                  {t('general.close')}
                </Button>
                <Button
                  type="submit"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={availableOptions.length === 0 || !isValid}
                  isLoading={isSubmitting || isProcessing}
                >
                  {t('general.btn-add-modal')}
                  <Icon icon="ki-send" size="fs-3" className="ms-2" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddChannelModal;
